import React, { ChangeEvent, useState } from "react";
import styles from "./IntentCard.module.css";
import clsx from "clsx";
import Metadata from "../../../../questionList/components/metaData/Metadata";
import { IntentDataModel } from "../../../../../../../../api/responsePayload/IntentResponse";
import CustomButton, {
  ButtonType,
} from "../../../../../../../../components/button/CustomButton";
import EditableField from "./EditableField";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../../../../store/common/store";
import { showConsoleError } from "../../../../../../../../util/ConsoleMessage";
import {
  DeleteIntentKnowledge,
  EditIntentKnowledge,
} from "../../../../../../../../api/IntentApiCall";
import { useAppDispatch } from "../../../../../../../../store/common/hooks";
import { fetchIntents } from "../../../../../../../../store/intent.slice";

interface IntentCardProps {
  intent: IntentDataModel;
}

const IntentCard: React.FC<IntentCardProps> = ({ intent }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localIntent, setLocalIntent] = useState(intent);
  const dispatch = useAppDispatch();
  const { currentPage } = useSelector((state: RootState) => state.pagination);

  const categoryOptions = [
    { name: "Unknown", value: 0 },
    { name: "Internal Jargon", value: 1 },
    { name: "Business Term", value: 2 },
    { name: "Rewrite Intent", value: 3 },
    { name: "Error Code", value: 4 },
  ];

  const handleEditClicked = () => {
    if (isEditing) {
      setLocalIntent(localIntent);
      if (
        localIntent.meaning.trim() === "" ||
        localIntent.detailed_input.trim() === "" ||
        localIntent.raw_input.length === 0
      ) {
        return null;
      }

      handleModify();
      setIsEditing((val) => !val);
    } else {
      setIsEditing((val) => !val);
    }
  };

  const handleOnChangeMeaning = (value: string) => {
    setLocalIntent((prevIntent) => ({
      ...prevIntent,
      meaning: value,
    }));
  };

  const handleOnChangeDetailedInput = (value: string) => {
    setLocalIntent((prevIntent) => ({
      ...prevIntent,
      detailed_input: value,
    }));
  };
  const handleOnCategoryChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setLocalIntent((prevIntent) => ({
      ...prevIntent,
      category: parseInt(event.target.value),
    }));
  };

  const handleModify = async () => {
    try {
      await EditIntentKnowledge(
        localIntent.id,
        localIntent.detailed_input,
        localIntent.raw_input,
        localIntent.category,
        localIntent.meaning
      );

      dispatch(fetchIntents({ page: currentPage }));
      setIsEditing(false);
    } catch (e) {
      showConsoleError(e);
    }
  };
  const handleOnRawInputChange = (values: string) => {
    setLocalIntent((prevIntent) => ({
      ...prevIntent,
      raw_input: values,
    }));
  };

  const handleDonePressed = () => {
    if (isEditing) {
      setIsEditing(false);
      setLocalIntent(intent);
    } else {
      handleReject();
    }
  };

  const handleReject = async () => {
    try {
      await DeleteIntentKnowledge(intent.id);
      dispatch(fetchIntents({ page: currentPage }));
    } catch (e) {
      showConsoleError(e);
    }
  };

  return (
    <div className={clsx(styles["question-group-container"])}>
      <div className={clsx(styles["question-group-main"])}>
        <Metadata
          text={`ID: ${localIntent.id} Date Created: ${localIntent.date_created} Date Updated: ${localIntent.last_updated}`}
        />
        <EditableField
          value={localIntent.raw_input}
          label="Raw Input:"
          isEditing={isEditing}
          onChange={handleOnRawInputChange}
        />
        <EditableField
          value={localIntent.meaning}
          label="Meaning:"
          isEditing={isEditing}
          onChange={handleOnChangeMeaning}
        />
        <EditableField
          value={localIntent.detailed_input}
          label="Detailed Input:"
          onChange={handleOnChangeDetailedInput}
          isEditing={isEditing}
        />
        <div className={styles["row-container"]}>
          <p>
            <strong>Category: </strong>
          </p>
          {isEditing ? (
            <div className={styles.field}>
              <select
                className={styles["category-select"]}
                value={localIntent.category}
                onChange={handleOnCategoryChange}
              >
                <option value="0">Select a category</option>
                {categoryOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.name}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            categoryOptions.find((val) => val.value === localIntent.category)
              ?.name
          )}
        </div>
        <div className={styles["buttons-container"]}>
          <CustomButton
            text={isEditing ? "Cancel" : "Delete"}
            type={ButtonType.Reject}
            onClick={handleDonePressed}
          />
          <CustomButton
            text={isEditing ? "Done" : "Edit"}
            type={isEditing ? ButtonType.Approve : ButtonType.Edit}
            onClick={handleEditClicked}
          />
        </div>
      </div>
    </div>
  );
};

export default IntentCard;
